import { NgClass, NgOptimizedImage } from '@angular/common';
import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
  MatButtonToggle,
  MatButtonToggleGroup,
} from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';
import { MatError, MatInput } from '@angular/material/input';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import SHA256 from 'crypto-js/sha256';
import moment from 'moment/moment';
import { ToastrService } from 'ngx-toastr';
import { AlphaNumericOnlyDirective } from '../../directives/alpha-numeric-only.directive';
import { ChineseEnglishSpaceOnlyDirective } from '../../directives/chinese-english-space-only.directive';
import { NumericOnlyDirective } from '../../directives/numeric-only.directive';
import { MaskIdNumberDirective } from '../../directives/mask-id-number.directive';
import { DuoActionDialogComponent } from '../../shared/components/dialogs/duo-action-dialog/duo-action-dialog.component';
import { ForgotPasswordDialogComponent } from '../../shared/components/dialogs/forgot-password-dialog/forgot-password-dialog.component';
import { OtpDialogComponent } from '../../shared/components/dialogs/otp-dialog/otp-dialog.component';
import { SingleActionDialogComponent } from '../../shared/components/dialogs/single-action-dialog/single-action-dialog.component';
import { API_PARAMS } from '../../shared/constants/api.constants';
import { LOGIN_REGISTER_TABS } from '../../shared/constants/common.constants';
import {
  API_DATE_FORMAT,
  DATE_FORMAT,
} from '../../shared/constants/date.constants';
import { STORAGE_KEY } from '../../shared/constants/store-key.constants';
import { CreateMemberRequest } from '../../shared/interfaces/set-password.model';
import { DevService } from '../../shared/services/dev.service';
import { LoginRegisterService } from '../../shared/services/login-register.service';
import { RegisterVerifyContactService } from '../../shared/services/register-verify-contact.service';
import { SharedStorageService } from '../../shared/services/shared-storage.service';
import {
  birthdayValidator,
  getGenderFromIdNo,
  idNoValidator,
  nameValidator,
  passwordValidator,
} from '../../shared/utils/validator.util';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { AuthService } from '../../shared/services/auth.service';
/**
 * 登入/註冊組件
 */
@Component({
  selector: 'app-login-register',
  standalone: true,
  imports: [
    MatTabGroup,
    MatTab,
    TranslatePipe,
    ReactiveFormsModule,
    MatInput,
    MatIconButton,
    MatError,
    MatButton,
    MatButtonToggleGroup,
    MatButtonToggle,
    FormsModule,
    MatRadioGroup,
    MatRadioButton,
    NgClass,
    NgOptimizedImage,
    MaskIdNumberDirective,
    NumericOnlyDirective,
    AlphaNumericOnlyDirective,
    ChineseEnglishSpaceOnlyDirective,
  ],
  templateUrl: './login-register.component.html',
  styleUrl: './login-register.component.scss',
})
export class LoginRegisterComponent implements OnInit, OnDestroy {
  showLoginFormError: boolean = false; // 是否顯示登入表單欄位錯誤
  showRegisterFormError: boolean = false; // 是否顯示註冊表單欄位錯誤

  // 生日三欄輸入（西元年 / 月 / 日）
  birthYear: string = '';
  birthMonth: string = '';
  birthDay: string = '';
  // 三欄都填完後，才即時顯示生日錯誤
  birthdayFilled: boolean = false;

  private isValidating: boolean = false; // 防止無限循環的標記
  private isLoginValidating: boolean = false; // 防止登入表單無限循環的標記
  isLoginProcessing: boolean = false; // 是否正在處理登入
  isRegisterProcessing: boolean = false; // 是否正在處理註冊
  translateService: TranslateService = inject(TranslateService);
  dialog: MatDialog = inject(MatDialog);
  toastr: ToastrService = inject(ToastrService);
  devService: DevService = inject(DevService);
  router: Router = inject(Router);
  route: ActivatedRoute = inject(ActivatedRoute);
  authService: AuthService = inject(AuthService);
  loginRegisterService: LoginRegisterService = inject(LoginRegisterService);
  registerVerifyContactService: RegisterVerifyContactService = inject(
    RegisterVerifyContactService
  );
  sharedStorageService: SharedStorageService = inject(SharedStorageService);

  // 預設頁籤 0: 登入 1: 註冊
  defaultTabIndex: string = !this.route.snapshot.queryParamMap.get('tab')
    ? '0'
    : this.route.snapshot.queryParamMap.get('tab') === LOGIN_REGISTER_TABS.LOGIN
      ? '0'
      : '1';
  idNumberMaxLength: number = 10; // 身分證最大長度
  passwordMinLength: number = 8; // 密碼最小長度
  passwordMaxLength: number = 20; // 密碼最大長度
  nameMaxLength: number = 50; // 姓名最大長度

  /**
   * -1: pass
   * 1: 帳號已暫時鎖定
   * 2: 驗證碼請求次數過多，請稍後再試
   * 3. 帳號或密碼錯誤
   * 4: 此ID已經有健檢預約
   * 5: 此ID已在150天內完成健檢
   * 6: 帳號尚未註冊
   * **/
  loginCondition: number = -1; // 登入結果

  /**
   * 0: 未註冊新帳號國泰健檢
   * 1: 已經註冊新帳號國泰健檢
   * 2: HIS有資料，跳到register-verify-contact.component
   */
  registerCondition?: string; // 註冊結果

  phoneNumber: string = ''; // user phone
  email: string = ''; // user email

  // 登入表單
  loginForm = new FormGroup({
    idNumber: new FormControl<string>('', [
      Validators.required,
      (control: FormControl) => idNoValidator(control, this.translateService),
    ]),
    password: new FormControl<string>('', [
      Validators.required,
      (control: FormControl) =>
        passwordValidator(control, this.translateService, false),
    ]),
  });

  // 註冊表單
  registerForm = new FormGroup({
    gender: new FormControl<string>(API_PARAMS.GENDER.MALE, [
      Validators.required,
    ]),
    birthday: new FormControl<moment.Moment | null>(null, [
      Validators.required,
      (control: FormControl) =>
        birthdayValidator(control, this.translateService, {
          max: 100,
          min: 18,
        }),
    ]),
    name: new FormControl('', [
      Validators.required,
      noSpecialCharsValidator,
      (control: FormControl) =>
        nameValidator(control, this.translateService, this.nameMaxLength),
    ]),
    idNumber: new FormControl('', [
      Validators.required,
      noSpecialCharsValidator,
      (control: FormControl) => idNoValidator(control, this.translateService),
    ]),
  });

  isLoginIdNumberVisible: boolean = false; // 是否顯示登入頁籤的身分證號碼欄位
  // 切換登入頁籤的身分證號碼欄位顯示狀態
  toggleLoginIdNumberVisibility(): void {
    this.isLoginIdNumberVisible = !this.isLoginIdNumberVisible;
  }

  isPasswordVisible: boolean = false; // 是否顯示登入頁籤的密碼欄位
  // 切換登入頁籤的密碼欄位顯示狀態
  togglePasswordVisibility(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  isRegisterIdNumberVisible: boolean = false; // 是否顯示註冊頁籤的身分證號碼欄位

  // 切換註冊頁籤的身分證號碼欄位顯示狀態
  toggleRegisterIdNumberVisibility(): void {
    this.isRegisterIdNumberVisible = !this.isRegisterIdNumberVisible;
  }

  ngOnInit(): void {
    const reserveStep = this.sharedStorageService.get('reserveStep');
    console.log('reserveStep:', reserveStep);

    // 只在使用者開始輸入時重置錯誤顯示標記
    this.loginForm.get('idNumber')?.valueChanges.subscribe(() => {
      if (this.showLoginFormError) {
        this.showLoginFormError = false;
      }
    });

    this.registerForm.get('idNumber')?.valueChanges.subscribe(idNumber => {
      if (this.showRegisterFormError) {
        this.showRegisterFormError = false;
      }

      // 由身分證第 2 碼自動帶入性別（1/8=男、2/9=女）；
      // 若 gender 欄位被停用（例如預約中註冊已鎖定）則不覆寫
      const genderControl = this.registerForm.get('gender');
      if (genderControl && genderControl.enabled) {
        const gender = getGenderFromIdNo(idNumber);
        if (gender && genderControl.value !== gender) {
          genderControl.setValue(gender, { emitEvent: false });
        }
      }
    });

    // 監聽登入表單變化,觸發即時驗證
    this.loginForm.valueChanges.subscribe(() => {
      if (this.showLoginFormError && !this.isLoginValidating) {
        this.isLoginValidating = true;

        Object.keys(this.loginForm.controls).forEach(key => {
          this.loginForm.get(key)?.updateValueAndValidity({ emitEvent: false });
        });

        setTimeout(() => {
          this.isLoginValidating = false;
        }, 0);
      }
    });

    // 監聽註冊表單變化，觸發即時驗證
    this.registerForm.valueChanges.subscribe(() => {
      if (this.showRegisterFormError && !this.isValidating) {
        this.isValidating = true;

        // 觸發所有欄位重新驗證
        Object.keys(this.registerForm.controls).forEach(key => {
          this.registerForm
            .get(key)
            ?.updateValueAndValidity({ emitEvent: false });
        });

        // 重置標記，允許下次驗證
        setTimeout(() => {
          this.isValidating = false;
        }, 0);
      }
    });

    if (this.defaultTabIndex === '1') {
      const memberData = this.sharedStorageService.get(
        STORAGE_KEY.CREATE_MEMBER_REQUEST
      );
      // if this page is navigate from user fill forget password form and found out the id is not registered yet, nicely fill id and birth from previous stage for user
      if (memberData?.idNo || memberData?.birthday) {
        this.registerForm.get('idNumber')?.setValue(memberData.idNo || '');
        const memberBirthday = moment(memberData.birthday, API_DATE_FORMAT);
        this.registerForm.get('birthday')?.setValue(memberBirthday || null);
        if (memberBirthday.isValid()) {
          this.birthYear = memberBirthday.format('YYYY');
          this.birthMonth = memberBirthday.format('MM');
          this.birthDay = memberBirthday.format('DD');
        }
      }

      // for register during the appointment, fill in the value of birthday and gender with what user fill in reserve-info page
      const userPlanRequest = this.sharedStorageService.get(
        STORAGE_KEY.USER_PLAN_REQUEST
      );
      const loginStatus = this.sharedStorageService.get(
        STORAGE_KEY.LOGIN_STATUS
      );
      //判斷是否為企業式登入
      const reserveType = this.sharedStorageService.get(
        STORAGE_KEY.RESERVE_TYPE
      );
      const isBusiness = reserveType === '2';
      if (
        (loginStatus === 'newFriend'|| loginStatus === undefined) &&
        (userPlanRequest?.birthday || userPlanRequest?.gender)
      ) {
        this.registerForm.get('gender')?.setValue(userPlanRequest.gender);
        this.registerForm.get('gender')?.disable();
        const planBirthday = moment(userPlanRequest.birthday, API_DATE_FORMAT);
        this.registerForm.get('birthday')?.setValue(planBirthday);
         //判斷是否為企業式登入 企業登入多填姓名與身分證
        if(isBusiness){
          this.registerForm.get('name')?.setValue(userPlanRequest.name);
          this.registerForm.get('idNumber')?.setValue(userPlanRequest.idNo);
        }
        if (planBirthday.isValid()) {
          this.birthYear = planBirthday.format('YYYY');
          this.birthMonth = planBirthday.format('MM');
          this.birthDay = planBirthday.format('DD');
        }
        this.registerForm.get('birthday')?.disable();
          //判斷是否為企業式登入 企業登入多disable姓名與身分證
        if(isBusiness){
          this.registerForm.get('name')?.disable();
          this.registerForm.get('idNumber')?.disable();
        }
      }

      // 曾經來過(withHIS)/無HIS(withoutHIS)：帶入 id-login 已輸入的姓名、身分證、生日並鎖定
      if (loginStatus === 'withHIS' || loginStatus === 'withoutHIS') {
        const idLoginRequest = this.sharedStorageService.get(
          STORAGE_KEY.ID_LOGIN_REQUEST
        );

        if (idLoginRequest) {
          this.registerForm.get('name')?.setValue(idLoginRequest.name ?? '');
          this.registerForm
            .get('idNumber')
            ?.setValue(idLoginRequest.idNo ?? '');

          const loginBirthday = moment(
            userPlanRequest?.birthday,
            API_DATE_FORMAT
          );
          this.registerForm
            .get('birthday')
            ?.setValue(loginBirthday.isValid() ? loginBirthday : null);
          if (loginBirthday.isValid()) {
            this.birthYear = loginBirthday.format('YYYY');
            this.birthMonth = loginBirthday.format('MM');
            this.birthDay = loginBirthday.format('DD');
          }

          this.registerForm.get('name')?.disable();
          this.registerForm.get('idNumber')?.disable();
          this.registerForm.get('birthday')?.disable();
        }
      }
    }
  }

  ngOnDestroy(): void {
    // 組件銷毀時的清理工作
  }

  // 獲取登入表單驗證身份證號錯誤訊息
  getLoginFormIdNumberErrorMessage(): string {
    const control = this.loginForm.get('idNumber');
    if (!control || !control.errors || !this.showLoginFormError) return '';

    const errors = control.errors;
    if (errors['required']) {
      return this.translateService.instant('error.id-number-error.required');
    }
    if (errors['invalidCharacters']) {
      return errors['invalidCharacters'];
    }
    return '';
  }

  // 獲取密碼錯誤訊息
  getPasswordErrorMessage(): string {
    const control = this.loginForm.get('password');
    if (!control || !control.errors || !this.showLoginFormError) return '';

    const errors = control.errors;
    if (errors['required']) {
      return this.translateService.instant('error.password-error.required');
    }
    if (errors['format']) {
      return errors['format'];
    }
    if (errors['minlength']) {
      return this.translateService.instant('error.min-length', {
        fieldName: this.translateService.instant('login-register.password'),
        minLength: this.passwordMinLength,
      });
    }
    if (errors['maxlength']) {
      return this.translateService.instant('error.max-length', {
        fieldName: this.translateService.instant('login-register.password'),
        maxLength: this.passwordMaxLength,
      });
    }
    return '';
  }

  /**
   * 生日三欄（年/月/日）任一欄變動時，組出日期並寫回 birthday FormControl。
   * 維持 moment.Moment 型別，讓既有的驗證器、送出邏輯都不需更動。
   */
  onBirthdayPartChange(): void {
    const year = this.birthYear.trim();
    const month = this.birthMonth.trim();
    const day = this.birthDay.trim();
    const control = this.registerForm.get('birthday');

    // 三欄都填完後才開始即時顯示錯誤
    this.birthdayFilled = !!year && !!month && !!day;

    if (!year || !month || !day) {
      control?.setValue(null);
      control?.markAsTouched();
      return;
    }

    // 補零成 YYYY/MM/DD，使用 moment 嚴格模式驗證（可正確判斷閏年、大小月）
    const dateStr = `${year.padStart(4, '0')}/${month.padStart(2, '0')}/${day.padStart(2, '0')}`;
    const parsed = moment(dateStr, DATE_FORMAT, true);

    control?.setValue(parsed.isValid() ? parsed : moment.invalid());
    control?.markAsTouched();
    control?.updateValueAndValidity();
  }

  /**
   * 生日欄位填滿時自動聚焦下一欄。
   * 年填滿 4 碼 → 跳月；月填滿 2 碼 → 跳日。
   * @param field 目前欄位（'year' | 'month'）
   * @param nextInput 下一欄的 input 元素
   */
  autoFocusNext(field: 'year' | 'month', nextInput: HTMLInputElement): void {
    const filled =
      field === 'year'
        ? this.birthYear.trim().length === 4
        : this.birthMonth.trim().length === 2;
    if (filled && nextInput && !nextInput.disabled) {
      nextInput.focus();
    }
  }

  /** 三欄是否都為空 */
  get isBirthdayAllEmpty(): boolean {
    return (
      !this.birthYear.trim() && !this.birthMonth.trim() && !this.birthDay.trim()
    );
  }

  /** 生日是否被鎖定（預約流程帶入後不可修改）→ 供模板加灰底樣式 */
  get isBirthdayDisabled(): boolean {
    return this.registerForm.get('birthday')?.disabled ?? false;
  }

  /** 送出後仍三欄皆空 → 需要三欄一起標紅、顯示年份提示 */
  get showBirthdayEmptyError(): boolean {
    return this.showRegisterFormError && this.isBirthdayAllEmpty;
  }

  /** 年份是否不合法（已填但非 4 碼、或超出 1900~今年） */
  get isYearInvalid(): boolean {
    const year = this.birthYear.trim();
    if (!year) return false;
    const y = Number(year);
    return year.length !== 4 || isNaN(y) || y < 1900 || y > moment().year();
  }

  /** 月份是否不合法（已填但不在 1~12） */
  get isMonthInvalid(): boolean {
    const month = this.birthMonth.trim();
    if (!month) return false;
    const m = Number(month);
    return isNaN(m) || m < 1 || m > 12;
  }

  /** 日期是否不合法（已填但不在 1~該年月天數；閏年、大小月由 moment 判斷） */
  get isDayInvalid(): boolean {
    const day = this.birthDay.trim();
    if (!day) return false;
    const d = Number(day);
    if (isNaN(d) || d < 1) return true;

    if (
      !this.isYearInvalid &&
      !this.isMonthInvalid &&
      this.birthYear.trim() &&
      this.birthMonth.trim()
    ) {
      const daysInMonth = moment(
        `${this.birthYear.trim().padStart(4, '0')}-${this.birthMonth.trim().padStart(2, '0')}`,
        'YYYY-MM'
      ).daysInMonth();
      return d > daysInMonth;
    }
    return d > 31;
  }

  // 獲取驗證生日錯誤訊息
  getBirthdayErrorMessage(): string {
    const control = this.registerForm.get('birthday');
    if (
      !control ||
      !control.errors ||
      (!this.showRegisterFormError && !this.birthdayFilled)
    )
      return '';

    const errors = control.errors;

    // mat-datepickerParse錯誤
    if (errors['matDatepickerParse']) {
      return this.translateService.instant('error.birthday-error.format');
    }
    if (errors['required']) {
      return this.translateService.instant('error.birthday-error.required');
    }
    if (errors['invalidFormat']) {
      return errors['invalidFormat'];
    }
    if (errors['invalidAge']) {
      return errors['invalidAge'];
    }
    return '';
  }

  // 獲取驗證姓名錯誤訊息
  getNameErrorMessage(): string {
    const control = this.registerForm.get('name');
    if (!control || !control.errors || !this.showRegisterFormError) return '';

    const errors = control.errors;
    if (errors['required']) {
      return this.translateService.instant('error.required', {
        fieldName: this.translateService.instant('login-register.name'),
      });
    }
    if (errors['invalidCharacters']) {
      return errors['invalidCharacters'];
    }
    if (errors['maxLengthExceeded']) {
      return errors['maxLengthExceeded'];
    }
    return '';
  }

  // 獲取註冊表單驗證身份證號錯誤訊息
  getRegisterFormIdNumberErrorMessage(): string {
    const control = this.registerForm.get('idNumber');
    if (!control || !control.errors || !this.showRegisterFormError) return '';

    const errors = control.errors;
    if (errors['required']) {
      return this.translateService.instant('error.id-number-error.required');
    }
    if (errors['invalidCharacters']) {
      return errors['invalidCharacters'];
    }
    return '';
  }

  // 登入表單提交
  onLoginSubmit() {
    if (this.isLoginProcessing) {
      return;
    }

    this.showLoginFormError = true;
    if (this.loginForm.valid) {
      this.isLoginProcessing = true;

      const idNumber = this.loginForm.get('idNumber')?.value ?? '';
      const password = this.loginForm.get('password')?.value ?? '';
      const encryptedPassword = SHA256(password).toString();

      this.loginRegisterService.login(idNumber, encryptedPassword).subscribe({
        next: res => {
          if (res.statusCode === 'S0000') {
            // login success - 不要重置，讓 OTP dialog 控制流程
            this.loginCondition = -1;
            this.showLoginFormError = false;
            this.openLoginOTPDialog(idNumber, res.data?.pwdReset);
          } else if (res.statusCode === 'EC1008') {
            // pwd wrong - 失敗才重置
            this.loginCondition = 3;
            this.isLoginProcessing = false;
            this.openErrorDialog();
          } else if (
            res.statusCode === 'E9011' ||
            res.statusCode === 'EC1000'
          ) {
            // user not found
            this.loginCondition = 6;
            this.isLoginProcessing = false;
            this.openErrorDialog();
          } else if (res.statusCode === 'EC1007') {
            // pwd attempt reach limit
            this.loginCondition = 1;
            this.isLoginProcessing = false;
            this.openErrorDialog();
          }
        },
        error: error => {
          console.error('Login error:', error);
          this.isLoginProcessing = false;
        },
      });
    }
  }

  // 註冊表單提交
  onRegisterSubmit() {
    if (this.isRegisterProcessing) {
      return;
    }
    this.showRegisterFormError = true;
    if (!this.registerForm.invalid) {
      // 讀值要用 getRawValue，因 withHIS/withoutHIS 時姓名/身分證/生日被 disable
      const raw = this.registerForm.getRawValue();
      const idNumber = raw.idNumber ?? '';
      const birthday = raw.birthday
        ? moment(raw.birthday).format(API_DATE_FORMAT)
        : '';

      // 曾經來過(withHIS)/無HIS(withoutHIS)：只多經過註冊頁帶入資料，
      // 不建會員、不發 OTP，直接回原本的聯絡資訊確認頁(contactinfo)
      const loginStatus = this.sharedStorageService.get(
        STORAGE_KEY.LOGIN_STATUS
      );
      if (loginStatus === 'withHIS' || loginStatus === 'withoutHIS') {
        const createMemberRequest: CreateMemberRequest = {
          gender: raw.gender ?? '',
          birthday: birthday,
          name: raw.name ?? '',
          idNo: idNumber,
          cell: '',
          email: '',
          password: '',
          confirmPassword: '',
        };
        this.sharedStorageService.set(
          STORAGE_KEY.CREATE_MEMBER_REQUEST,
          createMemberRequest
        );
        this.router.navigate(['/healthcheck/contactinfo']).then();
        return;
      }

      this.isRegisterProcessing = true;
      // function to check if this id has already member, has HIS etc.
      this.checkIfRegister(idNumber, birthday);
    }
  }

  checkIfRegister(idNumber: string, birthday: string) {
    const createMemberRequest: CreateMemberRequest = {
      gender: this.registerForm.get('gender')?.value ?? '',
      birthday: birthday,
      name: this.registerForm.get('name')?.value ?? '',
      idNo: idNumber,
      cell: '',
      email: '',
      password: '',
      confirmPassword: '',
    };
    this.sharedStorageService.set(
      STORAGE_KEY.CREATE_MEMBER_REQUEST,
      createMemberRequest
    );

    const loginStatus = this.sharedStorageService.get('loginStatus');

    this.loginRegisterService.checkIfRegistered(idNumber).subscribe({
      next: res => {
        this.registerCondition =
          res.statusCode === 'S0000'
            ? res.data?.flowCode
            : res.statusCode === 'EC1000'
              ? '0'
              : '';

        if (this.registerCondition === '0') {
          // new account
          this.isRegisterProcessing = false;
          if (loginStatus === 'newFriend') {
            // if it's login or register during the appointment we need to call CB-057 to check the appointment status first
            this.checkAppointmentHistory(idNumber, birthday, 'register', false);
          } else {
            this.sharedStorageService.set(
              STORAGE_KEY.LOGIN_STATUS,
              'withoutHIS'
            );
            this.router.navigate(['/healthcheck/contactinfo']).then(() => {});
          }
        } else if (this.registerCondition === '1') {
          // already a member
          this.isRegisterProcessing = false;
          // this account is already exist in new cathay system, show dialog to let user login instead
          this.dialog.open(SingleActionDialogComponent, {
            disableClose: true,
            data: {
              title: this.translateService.instant(
                'login-register.already-have-account'
              ),
              message: '',
              showCloseButton: true,
              buttonText: this.translateService.instant(
                'common.button.go-to-login'
              ),
              action: () => {
                this.router
                  .navigateByUrl('/healthcheck', { skipLocationChange: true })
                  .then(() => {
                    this.router
                      .navigate(['/healthcheck/login'], {
                        queryParams: { tab: LOGIN_REGISTER_TABS.LOGIN },
                      })
                      .then(() => this.closeDialog());
                  });
              },
            },
          });
        } else if (this.registerCondition === '2') {
          // data already exist in old HIS, but not yet registered in new cathay system
          const cellPhone = res.data?.cell ?? '';
          const emailAddress = res.data?.email ?? '';

          // 驗證手機號碼格式（10位數字且不為'0'）
          const hasValidPhone = !!(
            cellPhone &&
            cellPhone !== '0' &&
            /^09.{8}$/.test(cellPhone)
          );
          const hasValidEmail = emailAddress && emailAddress.trim() !== '';

          if (!hasValidPhone && !hasValidEmail) {
            // has HIS, but have no contact of both phone and email, call customer service
            this.isRegisterProcessing = false;
            this.openCallServiceDialog();
            return;
          }

          // 只有驗證通過才賦值，否則設為空字串
          this.phoneNumber = hasValidPhone ? cellPhone : '';
          this.email = hasValidEmail ? emailAddress : '';

          createMemberRequest.cell = this.phoneNumber;
          createMemberRequest.email = this.email;

          this.sharedStorageService.set(
            STORAGE_KEY.CREATE_MEMBER_REQUEST,
            createMemberRequest
          );

          // 根據來源決定流程
          const currentUrl = this.route.snapshot.url
            .map(segment => segment.path)
            .join('/'); // 轉成字串，如 'pathA/subpath'
          console.log('來源網址currentUrl:', currentUrl);
          if (currentUrl !== 'healthcheck/login') {
            // 預約流程：先檢查預約歷史，通過後才發送 OTP
            console.log('預約流程：先檢查預約歷史，通過後才發送 OTP');
            this.checkAppointmentHistoryBeforeSendingOtp(idNumber, birthday);
          } else {
            // 會員中心註冊：直接發送 OTP，不檢查預約
            console.log('會員中心註冊：直接發送 OTP，不檢查預約');
            this.sendOtpForRegistration(idNumber);
          }
        } else if (this.registerCondition === '3') {
          // 無聯絡方式，需客服
          this.isRegisterProcessing = false;
          this.openCallServiceDialog();
        }
      },
      error: error => {
        console.error('Register check error:', error);
        this.isRegisterProcessing = false;
      },
    });
  }

  // 開啟錯誤顯示Dialog
  openErrorDialog(action?: 'login' | 'register') {
    let title: string = '';
    let message: string = '';
    let buttonText: string = this.translateService.instant(
      'common.button.confirm'
    );
    let showCloseButton: boolean = true;

    if (this.loginCondition === 1) {
      title = this.translateService.instant('error.account-locked.title');
      message = this.translateService.instant('error.account-locked.message');
      showCloseButton = false;
    } else if (this.loginCondition === 2) {
      title = this.translateService.instant(
        'error.otp-error.reached-otp-limit'
      );
    } else if (this.loginCondition === 3) {
      title = this.translateService.instant('error.id-or-password-error.title');
      message = this.translateService.instant(
        'error.id-or-password-error.message'
      );
    } else if (this.loginCondition === 4) {
      showCloseButton = false;

      if (action === 'register') {
        title = this.translateService.instant(
          'id-login.already-reserved-dialog.title'
        );
        message = this.translateService.instant(
          'id-login.already-reserved-dialog.message'
        );
      }
      // 當從登入過來已有預約
      else if (action === 'login') {
        const reserveStep = this.sharedStorageService.get('reserveStep');

        // 如果是在預約流程中登入且已有預約，顯示特定訊息
        if (reserveStep === 5) {
          showCloseButton = true;
          title = this.translateService.instant(
            'id-login.login-already-reserved-dialog.title'
          );
          message = this.translateService.instant(
            'id-login.login-already-reserved-dialog.message'
          );
          buttonText = this.translateService.instant(
            'id-login.go-to-member-center'
          );
        } else {
          // 判斷是從我的健康檔案還是本次預約進去
          if (this.sharedStorageService.get('myHealthProfile') === true) {
            this.router.navigate(['/healthcheck/member/myprofile']).then();
          } else {
            this.router.navigate(['/healthcheck/member/mybooking']).then();
          }
          this.closeDialog();
          return;
        }
      }
    }
    // else if (this.loginCondition === 5) {
    //   title = this.translateService.instant(
    //     'id-login.already-complete-recently.title'
    //   );
    //   message = this.translateService.instant(
    //     'id-login.already-complete-recently.message'
    //   );
    // }
    else if (this.loginCondition === 6) {
      title = this.translateService.instant(
        'login-register.not-registered-title'
      );
      message = this.translateService.instant(
        'login-register.not-registered-message'
      );
      buttonText = this.translateService.instant(
        'common.button.go-to-register'
      );
      showCloseButton = false;
    } else if (this.loginCondition === 7) {
      // 新增：驗證碼請求時間過近
      title = this.translateService.instant('error.try-again-later.title');
      message = this.translateService.instant('error.try-again-later.message');
      showCloseButton = true;
    }

    this.dialog.open(SingleActionDialogComponent, {
      disableClose: true,
      data: {
        title: title,
        message: message,
        showCloseButton: showCloseButton,
        buttonText: buttonText,
        action: () => {
          this.closeDialog();

          // if it the already has appointment scenario, navigate to member page (try to login) or make user login (try to register) to check the exist appointment
          if (this.loginCondition === 4) {
            if (action === 'login') {
              if (this.sharedStorageService.get('myHealthProfile') === true) {
                this.router
                  .navigate(['/healthcheck/smember/myprofile'])
                  .then(() => this.closeDialog());
              } else {
                this.router
                  .navigate(['/healthcheck/member/mybooking'])
                  .then(() => this.closeDialog());
              }
            } else if (action === 'register') {
              this.router
                .navigateByUrl('/healthcheck', { skipLocationChange: true })
                .then(() => {
                  this.router.navigate(['/healthcheck/login'], {
                    queryParams: { tab: LOGIN_REGISTER_TABS.LOGIN },
                  });
                });
            }
          }
          // else if (this.loginCondition === 5) {
          //   this.router.navigate(['']);
          // }
          else if (this.loginCondition === 6) {
            this.router
              .navigateByUrl('/healthcheck', { skipLocationChange: true })
              .then(() => {
                this.router.navigate(['/healthcheck/login'], {
                  queryParams: { tab: LOGIN_REGISTER_TABS.REGISTER },
                });
              });
          }
          // loginCondition === 7 不需要特別的導航，只是關閉 dialog
        },
      },
    });
  }

  // 開啟客服電話Dialog
  openCallServiceDialog() {
    this.dialog.open(SingleActionDialogComponent, {
      disableClose: true,
      data: {
        title: this.translateService.instant(
          'id-login.call-customer-service.title'
        ),
        message: this.translateService.instant(
          'id-login.call-customer-service.message'
        ),
        showCloseButton: true,
        buttonText: this.translateService.instant('common.button.confirm'),
        action: () => this.router.navigate(['/']).then(r => this.closeDialog()),
      },
    });
  }

  // 關閉Dialog
  closeDialog() {
    this.dialog.closeAll();
  }

  // after login otp validation or check register status check appointment history for this id
  checkAppointmentHistory(
    idNumber: string,
    birthday: string,
    action: 'login' | 'register',
    withHIS?: boolean
  ) {
    //CB-057
    this.loginRegisterService
      .checkAppointmentHistory(idNumber, birthday)
      .subscribe(res => {
        if (res.statusCode === 'S0000') {
          const flowCode = res.data?.flowCode;
          if (flowCode === '0') {
            // can contiune making appointment
            if (action === 'register') {
              if (!withHIS) {
                this.sharedStorageService.set(
                  STORAGE_KEY.LOGIN_STATUS,
                  'withoutHIS'
                );
                this.router
                  .navigate(['/healthcheck/contactinfo'], {
                    queryParams: { backToAppointment: 'true' },
                  })
                  .then(() => {});
              } else {
                this.router
                  .navigate(['/healthcheck/contactinfo'], {
                    queryParams: { backToAppointment: 'true' },
                  })
                  .then(() => this.closeDialog());
              }
            } else if (action === 'login') {
              // login during the appointment, replace uuid with actual id in userPlanRequest and continute to appointment-info page
              //走快速預約的登入，將會員資料createMemberRequest寫進userPlanRequest
              const userPlanRequest = this.sharedStorageService.get(
                STORAGE_KEY.USER_PLAN_REQUEST
              );
              const createMemberRequest = this.sharedStorageService.get(
                STORAGE_KEY.CREATE_MEMBER_REQUEST
              );
              if (userPlanRequest) {
                userPlanRequest.idNo = idNumber;
                userPlanRequest.name = createMemberRequest?.name ?? '';
                userPlanRequest.cell = createMemberRequest?.cell ?? '';
                userPlanRequest.email = createMemberRequest?.email ?? '';

                this.sharedStorageService.set(
                  STORAGE_KEY.USER_PLAN_REQUEST,
                  userPlanRequest
                );
                this.sharedStorageService.set('reserveStep', 6);
                this.router.navigate(['/healthcheck/reserve/bookinfo']).then();
                this.closeDialog();
              }
            }
          } else if (flowCode === '4') {
            // already make apoointment with this id
            this.loginCondition = 4;
            this.openErrorDialog(action);
          }
          // else if (flowCode === '5') {
          //   // already finished the examination within 150 days with this id
          //   this.loginCondition = 5;
          //   this.openErrorDialog();
          // }
        }
      });
  }

  // 開啟輸入OTP dialog, 登入情境用
  openLoginOTPDialog(idNumber: string, pwdReset?: boolean) {
    const channel = 'cell'; // default to cell

    // 只發送一次 OTP，自動判斷發送到手機或 email
    this.loginRegisterService.loginSendOtp(idNumber, channel).subscribe(res => {
      console.log('loginSendOtp response:', res);
      this.handleLoginOtpResponse(res, idNumber, pwdReset);
    });
  }

  private handleLoginOtpResponse(
    res: any,
    idNumber: string,
    pwdReset?: boolean
  ) {
    // 檢查是否有有效的手機號碼和信箱
    const hasValidPhone =
      res.data?.cell && res.data.cell.trim() !== '' && res.data.cell !== '0';
    const hasValidEmail = res.data?.email && res.data.email.trim() !== '';
    this.phoneNumber = hasValidPhone ? (res.data?.cell ?? '') : '';
    this.email = hasValidEmail ? (res.data?.email ?? '') : '';

    if (res.statusCode === 'S0000') {
      const dialogRef = this.dialog.open(OtpDialogComponent, {
        disableClose: true,
        data: {
          isOtpPhone: !!this.phoneNumber,
          canSwitch: (this.phoneNumber && this.email) || false,
          phoneNumber: this.phoneNumber,
          email: this.email,
          idNo: idNumber,
          isFromlogin: true,
          successAction: () => {
            this.isLoginProcessing = false;
            const loginStatus = this.sharedStorageService.get('loginStatus');

            if (loginStatus === 'newFriend') {
              this.toastr.success(
                this.translateService.instant('login-register.login-success')
              );

              const birthday = '19980713';
              console.log('birthday:', birthday);
              this.checkAppointmentHistory(idNumber, birthday, 'login');
              const userplanRequest = this.sharedStorageService.get(
                STORAGE_KEY.USER_PLAN_REQUEST
              );
              if (!userplanRequest?.birthday && !userplanRequest?.gender) {
                if (this.sharedStorageService.get('myHealthProfile') === true) {
                  this.router
                    .navigate(['/healthcheck/member/myprofile'])
                    .then(() => this.closeDialog());
                } else {
                  this.router
                    .navigate(['/healthcheck/member/mybooking'])
                    .then(() => this.closeDialog());
                }
              }
            } else {
              const redirectInfo = this.sharedStorageService.get(
                STORAGE_KEY.REDIRECT_TO_COMPANY_LOGIN
              );

              if (redirectInfo?.shouldRedirect) {
                // 清除標記
                this.sharedStorageService.remove(
                  STORAGE_KEY.REDIRECT_TO_COMPANY_LOGIN
                );

                this.sharedStorageService.set(
                  STORAGE_KEY.LOGIN_STATUS,
                  'memberLogin'
                );

                this.toastr.success(
                  this.translateService.instant('login-register.login-success')
                );

                // 導回首頁 2025.01.20 先回首頁重新填
                // 2026.05.07更新，導到本次預約
                this.router
                  .navigate(['/healthcheck/member/mybooking'])
                  .then(() => this.closeDialog());

                // // 導向企業登入頁面並帶入企業代碼和員工編號
                // this.router.navigate(['/healthcheck/companylogin'], {
                //   state: {
                //     enterpriseCode: redirectInfo.enterpriseCode,
                //     empId: redirectInfo.empId,
                //     familyFlag: redirectInfo.familyFlag,
                //   }
                // }).then(() => this.closeDialog());

                return;
              }

              if (pwdReset) {
                const skipPasswordReminder =
                  this.getPasswordReminderSkipStatus(idNumber);

                if (!skipPasswordReminder) {
                  this.openPasswordUpdateReminderDialog(idNumber);
                } else {
                  this.toastr.success(
                    this.translateService.instant(
                      'login-register.login-success'
                    )
                  );

                  this.router
                    .navigate(['/healthcheck/member/mybooking'])
                    .then(() => this.closeDialog());
                }
              } else {
                this.toastr.success(
                  this.translateService.instant('login-register.login-success')
                );
                if (this.sharedStorageService.get('myHealthProfile') === true) {
                  this.router
                    .navigate(['/healthcheck/member/myprofile'])
                    .then(() => this.closeDialog());
                } else {
                  this.router
                    .navigate(['/healthcheck/member/mybooking'])
                    .then(() => this.closeDialog());
                }
              }
            }

            // 檢查是否為會員登入（有 token 且有會員資料）
            this.sharedStorageService.set(
              STORAGE_KEY.LOGIN_STATUS,
              'memberLogin'
            );
          },
        },
      });

      dialogRef.afterClosed().subscribe(() => {
        this.isLoginProcessing = false;
      });
    } else if (res.statusCode === 'EC5004') {
      this.loginCondition = 2;
      this.isLoginProcessing = false;
      this.openErrorDialog();
    } else if (res.statusCode === 'EC5003') {
      this.loginCondition = 7;
      this.isLoginProcessing = false;
      this.openErrorDialog();
    }
  }

  openPasswordResetSuggestionDialog() {
    this.dialog.open(DuoActionDialogComponent, {
      disableClose: true,
      data: {
        title: this.translateService.instant(
          'set-password.password-reset-suggestion.title'
        ),
        message: this.translateService.instant(
          'set-password.password-reset-suggestion.message'
        ),
        leftButtonText: this.translateService.instant(
          'set-password.handle-later'
        ),
        rightButtonText: this.translateService.instant(
          'set-password.password-reset'
        ),
        leftButtonAction: () => {
          this.toastr.success(
            this.translateService.instant('login-register.login-success')
          );

          // TODO 改成前往會員中心
          this.router.navigate(['']).then(() => this.closeDialog());
        },
        rightButtonAction: () => {
          // go to reset set-password
          this.router
            .navigate(['/healthcheck/passwordset'], {
              queryParams: { 'setting-source': 'reset' },
            })
            .then(() => this.closeDialog());
        },
      },
    });
  }

  // 開啟忘記密碼Dialog
  openForgotPasswordDialog() {
    this.dialog.open(ForgotPasswordDialogComponent, {
      disableClose: true,
    });
  }

  protected readonly DATE_FORMAT = DATE_FORMAT;
  protected readonly API_PARAMS = API_PARAMS;

  // 開啟密碼更新提醒Dialog
  openPasswordUpdateReminderDialog(idNumber: string) {
    this.dialog.open(DuoActionDialogComponent, {
      disableClose: true,
      data: {
        title: this.translateService.instant('password-reminder.title'),
        message: this.translateService.instant('password-reminder.message'),
        leftButtonText: this.translateService.instant(
          'password-reminder.later'
        ),
        rightButtonText: this.translateService.instant(
          'password-reminder.change-now'
        ),
        leftButtonAction: () => {
          // 稍後再說：記錄此次選擇，但下次登入會再次提醒
          // Note：不設置永久跳過標記，所以下次登入仍會提醒
          this.toastr.success(
            this.translateService.instant('login-register.login-success')
          );
          this.router
            .navigate(['/healthcheck/member/mybooking'])
            .then(() => this.closeDialog());
        },
        rightButtonAction: () => {
          // 立即變更：導向設定密碼頁面
          this.router
            .navigate(['/healthcheck/passwordset'], {
              queryParams: { 'setting-source': 'reminder' },
            })
            .then(() => this.closeDialog());
        },
      },
    });
  }

  // 檢查密碼提醒跳過狀態
  private getPasswordReminderSkipStatus(idNumber: string): boolean {
    const key = `${STORAGE_KEY.SKIP_PASSWORD_REMINDER}_${idNumber}`;
    try {
      return (this.sharedStorageService as any).get(key) || false;
    } catch {
      return false;
    }
  }

  // 設置密碼提醒跳過狀態
  private setPasswordReminderSkipStatus(idNumber: string, skip: boolean): void {
    const key = `${STORAGE_KEY.SKIP_PASSWORD_REMINDER}_${idNumber}`;
    try {
      (this.sharedStorageService as any).set(key, skip);
    } catch (error) {
      console.warn('Failed to set password reminder skip status:', error);
    }
  }

  // 在發送 OTP 前檢查預約
  private checkAppointmentHistoryBeforeSendingOtp(
    idNumber: string,
    birthday: string
  ) {
    this.loginRegisterService
      .checkAppointmentHistory(idNumber, birthday)
      .subscribe(res => {
        if (res.statusCode === 'S0000') {
          const flowCode = res.data?.flowCode;

          if (flowCode === '4') {
            // 已有預約，顯示彈窗並導向登入
            this.loginCondition = 4;
            this.isRegisterProcessing = false;
            this.openErrorDialog('register');
          } else if (flowCode === '5') {
            // 150天內已完成健檢
            this.isRegisterProcessing = false;
            this.loginCondition = 5;
            this.openErrorDialog();
          } else {
            // flowCode === '0'，可以繼續，發送 OTP
            this.sendOtpForRegistration(idNumber);
          }
        } else {
          // API 錯誤，仍然發送 OTP
          this.sendOtpForRegistration(idNumber);
        }
      });
  }

  // 發送 OTP 的邏輯抽取出來
  private sendOtpForRegistration(idNumber: string) {
    // 再次驗證，確保不會傳入無效值
    const hasValidPhone = !!(
      this.phoneNumber &&
      this.phoneNumber !== '0' &&
      /^09.{8}$/.test(this.phoneNumber)
    );
    const hasValidEmail = !!(this.email && this.email.trim());

    const type = hasValidPhone ? API_PARAMS.PHONE : API_PARAMS.EMAIL;
    const contact = hasValidPhone ? this.phoneNumber : this.email;

    console.log('漸進註冊');
    console.log('contact:', contact);
    console.log('this.phoneNumber:', this.phoneNumber);
    console.log('this.email:', this.email);
    console.log('hasValidPhone:', hasValidPhone);
    console.log('hasValidEmail:', hasValidEmail);

    this.registerVerifyContactService
      .registerSendOtp(idNumber, type, contact)
      .subscribe(res => {
        if (res.statusCode === 'S0000') {
          const dialogRef = this.dialog.open(OtpDialogComponent, {
            disableClose: true,
            data: {
              isOtpPhone: hasValidPhone,
              canSwitch: hasValidPhone && hasValidEmail,
              phoneNumber: this.phoneNumber,
              email: this.email,
              idNo: idNumber,
              isFromlogin: false,
              successAction: () => {
                this.isRegisterProcessing = false;
                this.sharedStorageService.set(
                  STORAGE_KEY.LOGIN_STATUS,
                  'withHIS'
                );
                this.router
                  .navigate(['/healthcheck/contactinfo'])
                  .then(() => this.closeDialog());
              },
            },
          });

          // 處理用戶取消的情況
          dialogRef.afterClosed().subscribe(() => {
            this.isRegisterProcessing = false;
          });
        } else if (res.statusCode === 'EC5003') {
          //驗證碼請求時間過近
          this.loginCondition = 7;
          this.isRegisterProcessing = false;
          this.openErrorDialog();
        } else if (res.statusCode === 'EC5004') {
          // otp attempt reach limit
          this.isRegisterProcessing = false;
          this.loginCondition = 2;
          this.openErrorDialog();
        }
      });
  }
}
/** 檢查是否包含特殊符號或頭尾空白 */
export function noSpecialCharsValidator(
  control: AbstractControl
): ValidationErrors | null {
  const value = control.value;

  // 如果是 null 或空字串，不在這裡報錯，交給 Validators.required 處理
  if (value == null) return null;

  const trimmed = value.trim();

  // 若整個字串都是空白（例如 "   "）
  if (trimmed.length === 0) {
    return { onlyWhitespace: true };
  }

  // 若有頭尾空白
  if (trimmed !== value) {
    return { whitespace: true };
  }

  // 若含有特殊符號（允許中英文、數字、空白中間）
  const regex = /^[a-zA-Z0-9\u4e00-\u9fa5\s]+$/;
  if (!regex.test(value)) {
    return { specialChar: true };
  }

  return null;
}
